<template>
  <el-dialog
    v-model="visible"
    :title="`编辑区域标注 · ${mapInfo?.name || '测试地图'}`"
    width="1200px"
    :close-on-click-modal="false"
    class="area-annotation-dialog"
    @close="handleClose"
  >
    <div class="annotation-container">
      <el-row :gutter="24" class="full-height">
        <!-- 左侧：地图绘制区域 -->
        <el-col :span="14" class="left-canvas-col">
          <div 
            class="map-canvas" 
            ref="canvasRef"
            @click="handleCanvasClick"
            @mousemove="handleMouseMove"
          >
            <!-- 左上角底图切换（装饰） -->
            <div class="map-bg-switch">
              <el-button size="small">底图 <i class="el-icon-arrow-down"></i></el-button>
            </div>

            <!-- 1. 底层：真实地图图片 -->
            <img 
              v-if="mapInfo?.mapImageUrl || mapInfo?.mapImagePath" 
              :src="mapInfo.mapImageUrl || mapInfo.mapImagePath" 
              class="map-background-img"
              alt="map background"
              draggable="false"
            />
            <div v-else class="map-placeholder-bg">暂无地图图片</div>

            <!-- 2. 顶层：交互绘制 SVG -->
            <svg width="100%" height="100%" class="canvas-svg">
              <!-- 已保存的区域/点位 -->
              <g v-for="(poly, pIndex) in savedPolygons" :key="`saved-${pIndex}`">
                <!-- 情况A：多边形绘制（迎宾区 / 互动体验区） -->
                <template v-if="poly.type !== 'point' && poly.type !== 'charger'">
                  <polygon
                    :points="getPolygonPoints(poly.points)"
                    :fill="poly.type === 'work' ? 'rgba(230, 162, 60, 0.2)' : 'rgba(64, 158, 255, 0.2)'"
                    :stroke="poly.type === 'work' ? '#e6a23c' : '#409eff'"
                    stroke-width="2"
                  />
                  <text 
                    :x="getCenter(poly.points).x" 
                    :y="getCenter(poly.points).y" 
                    text-anchor="middle" 
                    dominant-baseline="middle"
                    font-size="14"
                    font-weight="bold"
                    fill="#fff"
                    style="text-shadow: 0 0 4px #000;"
                  >
                    {{ poly.name }}
                  </text>
                </template>
                <!-- 情况B：点位绘制（讲解点 / 充电桩） -->
                <template v-else>
                  <!-- 主点 -->
                  <circle 
                    :cx="poly.point.x" 
                    :cy="poly.point.y" 
                    r="5" 
                    :fill="poly.type === 'charger' ? '#67c23a' : '#409eff'" 
                    stroke="#fff" 
                    stroke-width="1.5" 
                  />
                  <text 
                    :x="poly.point.x" 
                    :y="poly.point.y - 10" 
                    text-anchor="middle" 
                    font-size="12"
                    fill="#303133"
                  >
                    {{ poly.name }}
                  </text>
                  
                  <!-- 备选点渲染 (仅讲解点有) -->
                  <template v-if="poly.type === 'point' && poly.isExpanded && poly.alternates && poly.alternates.length > 0">
                    <g v-for="(alt, altIndex) in poly.alternates" :key="`alt-${altIndex}`">
                      <!-- 虚线 -->
                      <line 
                        :x1="poly.point.x" 
                        :y1="poly.point.y" 
                        :x2="alt.x" 
                        :y2="alt.y" 
                        stroke="#909399" 
                        stroke-width="1.5" 
                        stroke-dasharray="4" 
                      />
                      <!-- 备选点空心圆 (支持拖拽) -->
                      <circle 
                        :cx="alt.x" 
                        :cy="alt.y" 
                        r="5" 
                        :fill="dragState.draggingIndex === altIndex ? '#409eff' : '#fff'" 
                        :stroke="dragState.draggingIndex === altIndex ? '#409eff' : '#909399'" 
                        stroke-width="1.5"
                        style="cursor: move;"
                        @mousedown.stop="startDrag($event, pIndex, altIndex)"
                      />
                      <!-- 备选点文字标记 -->
                      <text 
                        :x="alt.x - 8" 
                        :y="alt.y - 8" 
                        font-size="12"
                        fill="#909399"
                        style="pointer-events: none;"
                      >
                        备{{ altIndex + 1 }}
                      </text>
                    </g>
                  </template>
                </template>
              </g>

              <!-- 当前正在绘制的内容 -->
              <g v-if="currentPolygon.length > 0 || currentPoint">
                <!-- 绘制多边形 -->
                <template v-if="!['point', 'charger'].includes(form.type)">
                  <polyline
                    :points="getPolygonPoints(currentPolygon, false)"
                    :fill="form.type === 'work' ? 'rgba(230, 162, 60, 0.2)' : 'rgba(64, 158, 255, 0.2)'"
                    :stroke="form.type === 'work' ? '#e6a23c' : '#409eff'"
                    stroke-width="2"
                  />
                  <line
                    v-if="currentPolygon.length > 1"
                    :x1="currentPolygon[currentPolygon.length - 1].x"
                    :y1="currentPolygon[currentPolygon.length - 1].y"
                    :x2="hoverPos.x"
                    :y2="hoverPos.y"
                    :stroke="form.type === 'work' ? '#e6a23c' : '#409eff'"
                    stroke-width="2"
                    stroke-dasharray="4"
                  />
                  <g v-for="(pt, index) in currentPolygon" :key="`cur-pt-${index}`">
                    <circle :cx="pt.x" :cy="pt.y" r="10" :fill="form.type === 'work' ? '#e6a23c' : '#409eff'" stroke="#fff" stroke-width="2" />
                    <text :x="pt.x" :y="pt.y" fill="#fff" font-size="12" font-weight="bold" text-anchor="middle" dominant-baseline="central">{{ index + 1 }}</text>
                  </g>
                </template>
                <!-- 绘制单点 -->
                <template v-else>
                  <circle 
                    :cx="currentPoint.x" 
                    :cy="currentPoint.y" 
                    r="6" 
                    :fill="form.type === 'charger' ? '#67c23a' : '#409eff'" 
                    stroke="#fff" 
                    stroke-width="2" 
                  />
                </template>
              </g>
            </svg>
          </div>

          <!-- 左下角提示 -->
          <div class="map-tip-bar">
            <span class="dot"></span> 点击地图放置【{{ form.type === 'point' ? '讲解点' : (form.type === 'charger' ? '充电桩' : '区域') }}】，落点后可在下方坐标框微调，或再次点击地图移动
          </div>
        </el-col>

        <!-- 右侧：标注清单与表单 -->
        <el-col :span="10" class="right-panel-col">
          <!-- 区域标注清单 -->
          <div class="section-title">区域标注清单</div>
          <div class="annotation-list">
            <div v-for="(poly, index) in savedPolygons" :key="index" class="annotation-item" :class="{ 'is-point': poly.type === 'point' || poly.type === 'charger' }">
              
              <!-- 头部（通用） -->
              <div class="item-header">
                <div class="item-title" @click="toggleExpand(index)" style="cursor: pointer; flex: 1;">
                  <i class="css-arrow" :class="{ 'is-expanded': poly.isExpanded }" style="margin-right: 4px;"></i>
                  
                  <!-- 图标：区域 / 讲解点 / 充电桩 -->
                  <span v-if="poly.type === 'welcome'" class="icon-square-blue"></span>
                  <span v-else-if="poly.type === 'work'" class="icon-square-orange"></span>
                  <span v-else-if="poly.type === 'point'" class="icon-diamond"></span>
                  <span v-else class="icon-charger"></span>
                  
                  <span>{{ poly.name }}</span>
                  <span v-if="poly.type === 'point' || poly.type === 'charger'" class="meta-text" style="margin-left: 4px;">
                    ({{ poly.point.xPercent }}, {{ poly.point.yPercent }})
                  </span>
                </div>
                
                <!-- 右侧按钮操作区 -->
                <div class="item-actions">
                  <!-- 只有讲解点显示备选点按钮 -->
                  <template v-if="poly.type === 'point'">
                    <el-button 
                      type="primary" 
                      plain 
                      size="small" 
                      class="add-point-btn" 
                      :disabled="poly.alternates && poly.alternates.length >= 5"
                      @click.stop="handleAddAlternate(index)"
                    >
                      + 备选点
                    </el-button>
                  </template>
                  <el-button type="default" size="small" class="delete-btn" @click.stop="deletePolygon(index)">删除</el-button>
                </div>
              </div>
              
              <!-- 内容体 -->
              <div class="item-expand" v-show="poly.isExpanded">
                <!-- 情况A：多边形信息展示（迎宾区 / 互动体验区）- 纯粹展示顶点坐标 -->
                <template v-if="poly.type !== 'point' && poly.type !== 'charger'">
                  <div class="expand-trigger" style="cursor: default;">
                    顶点坐标 · {{ poly.points.length }}个
                  </div>
                  <el-table :data="poly.points" size="small" border class="vertex-table">
                    <el-table-column label="#" width="50" align="center">
                      <template #default="{ $index }">V{{ $index + 1 }}</template>
                    </el-table-column>
                    <el-table-column label="X(m)" align="center">
                      <template #default="{ row }">{{ row.realX !== undefined ? row.realX + 'm' : '-' }}</template>
                    </el-table-column>
                    <el-table-column label="Y(m)" align="center">
                      <template #default="{ row }">{{ row.realY !== undefined ? row.realY + 'm' : '-' }}</template>
                    </el-table-column>
                  </el-table>
                </template>

                <!-- 情况B：点位信息展示（讲解点 / 充电桩） -->
                <template v-else>
                  <!-- 坐标表格 -->
                  <el-table :data="getPointTableData(poly)" size="small" class="point-table compact-table" style="margin-bottom: 8px;">
                    <el-table-column label="" width="50" align="center">
                      <template #default="{ row }">
                        <span :class="row.isMain ? 'tag-main' : 'tag-alt'">{{ row.label }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="X" align="center">
                      <template #default="{ row }">{{ row.xPercent }}</template>
                    </el-table-column>
                    <el-table-column label="Y" align="center">
                      <template #default="{ row }">{{ row.yPercent }}</template>
                    </el-table-column>
                    <el-table-column label="Yaw" align="center">
                      <template #default="{ row }">{{ row.yaw || 0 }}</template>
                    </el-table-column>
                    <el-table-column label="世界坐标" align="center">
                      <template #default="{ row }">{{ row.worldCoord || '-' }}</template>
                    </el-table-column>
                    <!-- 备选点删除按钮 (只有讲解点有) -->
                    <el-table-column width="30" align="center" v-if="poly.type === 'point' && poly.alternates && poly.alternates.length > 0">
                      <template #default="{ row, $index }">
                        <el-button v-if="!row.isMain" type="text" size="small" class="del-alt-btn" @click="handleRemoveAlternate(index, $index - 1)">
                          ×
                        </el-button>
                      </template>
                    </el-table-column>
                  </el-table>

                  <!-- 行为绑定面板 (仅讲解点显示) -->
                  <div class="behavior-panel" v-if="poly.type === 'point'">
                    <div class="panel-title">行为绑定</div>
                    <el-form label-width="105px" size="small" class="behavior-form compact-behavior-form">
                      <el-form-item label="动作库">
                        <el-select v-model="poly.point.action" placeholder="—" style="width: 100%">
                          <el-option label="动作A" value="A" />
                        </el-select>
                      </el-form-item>
                      <el-form-item label="讲解词/播报内容">
                        <el-input v-model="poly.point.script" type="textarea" :rows="1" placeholder="" />
                      </el-form-item>
                      <el-form-item label="点位详情">
                        <el-input v-model="poly.point.detail" type="textarea" :rows="1" placeholder="" />
                      </el-form-item>
                      <!-- 点位标签 -->
                      <el-form-item label="点位标签">
                        <el-input 
                          v-model="poly.point.tagInput" 
                          placeholder="输入标签，回车添加" 
                          @keyup.enter="addTag(poly)"
                        >
                          <template #append>
                            <el-button icon="el-icon-plus" @click="addTag(poly)"></el-button>
                          </template>
                        </el-input>
                        <!-- 标签展示区 -->
                        <div class="tags-container" v-if="poly.point.tags && poly.point.tags.length > 0">
                          <el-tag
                            v-for="(tag, tIndex) in poly.point.tags"
                            :key="tIndex"
                            closable
                            size="small"
                            @close="removeTag(poly, tIndex)"
                            class="custom-tag"
                          >
                            {{ tag }}
                          </el-tag>
                        </div>
                      </el-form-item>
                    </el-form>
                  </div>
                </template>
              </div>
            </div>
            
            <el-empty v-if="savedPolygons.length === 0" description="暂无标注" :image-size="60" />
          </div>

          <!-- 新增区域标注（表单区） -->
          <div class="section-title">新增区域标注</div>
          <div class="new-annotation-form">
            <el-form label-width="70px" size="small" class="compact-form">
              <el-form-item label="区域类型" required>
                <el-select v-model="form.type" placeholder="请选择" style="width: 100%" @change="handleTypeChange">
                  <el-option label="迎宾区" value="welcome" />
                  <el-option label="互动体验区" value="work" />
                  <el-option label="讲解点" value="point" />
                  <el-option label="充电桩" value="charger" />
                </el-select>
              </el-form-item>
              <el-form-item label="区域名称" required>
                <el-input v-model="form.name" placeholder="如 入口迎宾 / 互动体验区" />
              </el-form-item>
              
              <!-- 单点专属坐标表单 (讲解点 或 充电桩) -->
              <template v-if="['point', 'charger'].includes(form.type)">
                <el-form-item label="坐标" required style="margin-bottom: 8px;">
                  <div style="display: flex; gap: 8px; width: 100%;">
                    <el-input :value="currentPoint ? currentPoint.xPercent : ''" placeholder="X">
                      <template #prepend>X</template>
                    </el-input>
                    <el-input :value="currentPoint ? currentPoint.yPercent : ''" placeholder="Y">
                      <template #prepend>Y</template>
                    </el-input>
                  </div>
                </el-form-item>
                <!-- 只有讲解点显示讲解词 -->
                <el-form-item label="讲解词" style="margin-bottom: 8px;" v-if="form.type === 'point'">
                  <el-input v-model="form.script" type="textarea" :rows="2" placeholder="讲解点播报文本，可先打点后补词" />
                </el-form-item>
              </template>
            </el-form>

            <!-- 底部操作栏：根据类型切换 -->
            <div class="canvas-actions" style="margin-top: 12px;">
              <template v-if="!['point', 'charger'].includes(form.type)">
                <span class="point-count">已落 {{ currentPolygon.length }} 点</span>
                <div class="action-btns">
                  <el-button size="small" @click="undoPoint" :disabled="currentPolygon.length === 0">撤销上一点</el-button>
                  <el-button size="small" @click="clearCurrent" :disabled="currentPolygon.length === 0">清空</el-button>
                  <el-button type="danger" size="small" @click="confirmPolygon" :disabled="currentPolygon.length < 3">闭合</el-button>
                </div>
              </template>
              <template v-else>
                <el-button type="danger" class="full-width-add-btn" @click="confirmPoint" :disabled="!currentPoint || !form.name">
                  + 添加
                </el-button>
              </template>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="danger" @click="handleSave">保存</el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'

export default {
  name: 'AreaAnnotation',
  props: {
    modelValue: { type: Boolean, default: false },
    mapInfo: { type: Object, default: () => ({}) }
  },
  emits: ['update:modelValue', 'success'],
  setup(props, { emit }) {
    const visible = computed({
      get: () => props.modelValue,
      set: (val) => emit('update:modelValue', val)
    })

    const canvasRef = ref(null)
    const currentPolygon = ref([])
    const currentPoint = ref(null)
    const hoverPos = ref({ x: 0, y: 0 })
    const savedPolygons = ref([])

    const form = reactive({
      type: 'welcome',
      name: '',
      isMeter: false,
      script: ''
    })

    // ====== 拖拽状态管理 ======
    const dragState = reactive({
      isDragging: false,
      polyIndex: -1, 
      draggingIndex: -1, 
      offsetX: 0, 
      offsetY: 0
    })

    const getRealOriginX = () => Number(props.mapInfo?.originX || -23.4)
    const getRealOriginY = () => Number(props.mapInfo?.originY || -20.9)
    const getResolution = () => Number(props.mapInfo?.resolution || 0.05)

    onMounted(() => {
      window.addEventListener('mousemove', handleDragMove)
      window.addEventListener('mouseup', handleDragEnd)
    })
    
    onUnmounted(() => {
      window.removeEventListener('mousemove', handleDragMove)
      window.removeEventListener('mouseup', handleDragEnd)
    })

    const startDrag = (e, pIndex, altIndex) => {
      if (!canvasRef.value) return;
      const rect = canvasRef.value.getBoundingClientRect();
      const alt = savedPolygons.value[pIndex].alternates[altIndex];
      
      dragState.isDragging = true;
      dragState.polyIndex = pIndex;
      dragState.draggingIndex = altIndex;
      dragState.offsetX = (e.clientX - rect.left) - alt.x;
      dragState.offsetY = (e.clientY - rect.top) - alt.y;
    }

    const handleDragMove = (e) => {
      if (!dragState.isDragging || !canvasRef.value) return;
      const rect = canvasRef.value.getBoundingClientRect();
      
      let newX = (e.clientX - rect.left) - dragState.offsetX;
      let newY = (e.clientY - rect.top) - dragState.offsetY;

      newX = Math.max(5, Math.min(rect.width - 5, newX));
      newY = Math.max(5, Math.min(rect.height - 5, newY));

      const xPercent = Number(((newX / rect.width) * 100).toFixed(2));
      const yPercent = Number(((newY / rect.height) * 100).toFixed(2));
      
      const worldX = (newX * getResolution() + getRealOriginX()).toFixed(2);
      const worldY = (newY * getResolution() + getRealOriginY()).toFixed(2);
      const worldCoord = `${worldX}m · ${worldY}m`;

      const alt = savedPolygons.value[dragState.polyIndex].alternates[dragState.draggingIndex];
      alt.x = newX;
      alt.y = newY;
      alt.xPercent = xPercent;
      alt.yPercent = yPercent;
      alt.worldCoord = worldCoord;
    }

    const handleDragEnd = () => {
      if (dragState.isDragging) {
        dragState.isDragging = false;
        dragState.polyIndex = -1;
        dragState.draggingIndex = -1;
      }
    }

    watch(visible, (val) => {
      if (val) {
        const annotations = props.mapInfo?.annotations || []
        savedPolygons.value = annotations.map((item, idx) => {
          if (item.type === 'point' && item.point) {
            if (!item.point.tags) item.point.tags = [];
            if (!item.point.tagInput) item.point.tagInput = '';
          }
          return {
            ...item,
            isExpanded: idx === 0,
            alternates: item.alternates || []
          }
        })
        currentPolygon.value = []
        currentPoint.value = null
        form.name = ''
        form.script = ''
        form.type = 'welcome'
      }
    })

    const handleTypeChange = () => {
      currentPolygon.value = []
      currentPoint.value = null
      form.name = ''
    }

    const toggleExpand = (index) => {
      savedPolygons.value[index].isExpanded = !savedPolygons.value[index].isExpanded
    }

    const handleMouseMove = (e) => {
      if (dragState.isDragging) return; 
      if (['point', 'charger'].includes(form.type) || currentPolygon.value.length === 0 || !canvasRef.value) return
      const rect = canvasRef.value.getBoundingClientRect()
      hoverPos.value = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const handleCanvasClick = (e) => {
      if (dragState.isDragging) return; 
      if (!canvasRef.value) return
      const rect = canvasRef.value.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return

      const xPercent = Number(((x / rect.width) * 100).toFixed(2));
      const yPercent = Number(((y / rect.height) * 100).toFixed(2));
      
      const worldX = (x * getResolution() + getRealOriginX()).toFixed(2);
      const worldY = (y * getResolution() + getRealOriginY()).toFixed(2);
      const worldCoord = `${worldX}m · ${worldY}m`;

      if (['point', 'charger'].includes(form.type)) {
        currentPoint.value = { x, y, xPercent, yPercent, worldCoord }
      } else {
        currentPolygon.value.push({ x, y })
      }
    }

    const undoPoint = () => {
      if (currentPolygon.value.length > 0) currentPolygon.value.pop()
    }

    const clearCurrent = () => {
      currentPolygon.value = []
    }

    const confirmPolygon = () => {
      if (!form.name) return ElMessage.warning('请输入区域名称')
      if (currentPolygon.value.length < 3) return ElMessage.warning('至少需要3个顶点')

      const resolution = getResolution()
      const originX = getRealOriginX()
      const originY = getRealOriginY()
      
      const realPoints = currentPolygon.value.map(p => ({
        x: p.x, y: p.y,
        realX: Number((p.x * resolution + originX).toFixed(4)),
        realY: Number((p.y * resolution + originY).toFixed(4))
      }))

      savedPolygons.value.push({
        type: form.type,
        name: form.name,
        points: realPoints,
        isExpanded: true
      })
      
      currentPolygon.value = []
      form.name = ''
      ElMessage.success('区域已添加')
    }

    const confirmPoint = () => {
      if (!form.name) return ElMessage.warning('请输入名称')
      if (!currentPoint.value) return ElMessage.warning('请在地图选点')

      savedPolygons.value.push({
        type: form.type, // 'point' 或 'charger'
        name: form.name,
        point: {
          x: currentPoint.value.x,
          y: currentPoint.value.y,
          xPercent: currentPoint.value.xPercent,
          yPercent: currentPoint.value.yPercent,
          yaw: 0,
          worldCoord: currentPoint.value.worldCoord,
          action: '',
          script: form.type === 'point' ? form.script : '', // 充电桩无需讲解词
          detail: '',
          tags: [],
          tagInput: ''
        },
        alternates: [],
        isExpanded: true
      })

      currentPoint.value = null
      form.name = ''
      form.script = ''
      ElMessage.success(`${form.type === 'charger' ? '充电桩' : '讲解点'}已添加`)
    }

    const handleAddAlternate = (index) => {
      const poly = savedPolygons.value[index];
      
      if (poly.alternates.length >= 5) {
        return ElMessage.warning('备选点最多只能添加 5 个');
      }
      
      if (!canvasRef.value) return;
      const rect = canvasRef.value.getBoundingClientRect();

      const offsetX = (Math.random() - 0.5) * 80;
      const offsetY = (Math.random() - 0.5) * 80;

      let newX = poly.point.x + offsetX;
      let newY = poly.point.y + offsetY;
      newX = Math.max(10, Math.min(rect.width - 10, newX));
      newY = Math.max(10, Math.min(rect.height - 10, newY));

      const xPercent = Number(((newX / rect.width) * 100).toFixed(2));
      const yPercent = Number(((newY / rect.height) * 100).toFixed(2));
      
      const worldX = (newX * getResolution() + getRealOriginX()).toFixed(2);
      const worldY = (newY * getResolution() + getRealOriginY()).toFixed(2);
      const worldCoord = `${worldX}m · ${worldY}m`;

      poly.alternates.push({
        x: newX,
        y: newY,
        xPercent,
        yPercent,
        yaw: 0,
        worldCoord
      });
      
      ElMessage.success(`备选点 ${poly.alternates.length} 已添加，可拖拽调整`);
    }

    const handleRemoveAlternate = (polyIndex, altIndex) => {
      savedPolygons.value[polyIndex].alternates.splice(altIndex, 1);
    }

    const getPointTableData = (poly) => {
      if (!poly || !poly.point) return [];
      const mainRow = {
        label: '主点',
        isMain: true,
        xPercent: poly.point.xPercent,
        yPercent: poly.point.yPercent,
        yaw: poly.point.yaw,
        worldCoord: poly.point.worldCoord
      };
      const altRows = (poly.alternates || []).map((alt, i) => ({
        label: `备选${i + 1}`,
        isMain: false,
        xPercent: alt.xPercent,
        yPercent: alt.yPercent,
        yaw: alt.yaw,
        worldCoord: alt.worldCoord
      }));
      return [mainRow, ...altRows];
    }

    const deletePolygon = (index) => {
      savedPolygons.value.splice(index, 1)
    }

    const addTag = (poly) => {
      const val = poly.point.tagInput?.trim();
      if (!val) return;
      if (!poly.point.tags) poly.point.tags = [];
      if (poly.point.tags.includes(val)) {
        ElMessage.warning('该标签已存在');
      } else {
        poly.point.tags.push(val);
      }
      poly.point.tagInput = '';
    }

    const removeTag = (poly, index) => {
      poly.point.tags.splice(index, 1);
    }

    const handleSave = () => {
      if (currentPolygon.value.length > 0 || currentPoint.value) {
        return ElMessage.warning('有未完成的绘制，请先确认或清空')
      }
      const result = savedPolygons.value.map(({ isExpanded, ...rest }) => {
        if ((rest.type === 'point' || rest.type === 'charger') && rest.point) {
          delete rest.point.tagInput;
        }
        return rest;
      })
      emit('success', result)
      visible.value = false
    }

    const handleClose = () => {
      visible.value = false
    }

    const getPolygonPoints = (points, close = true) => {
      if (!points || points.length === 0) return ''
      const str = points.map(p => `${p.x},${p.y}`).join(' ')
      return close ? `${str}` : str
    }

    const getCenter = (points) => {
      if (!points || points.length === 0) return { x: 0, y: 0 }
      let sumX = 0, sumY = 0
      points.forEach(p => { sumX += p.x; sumY += p.y })
      return { x: sumX / points.length, y: sumY / points.length }
    }

    return {
      visible,
      canvasRef,
      currentPolygon,
      currentPoint,
      hoverPos,
      savedPolygons,
      form,
      dragState,
      startDrag,
      handleTypeChange,
      toggleExpand,
      handleMouseMove,
      handleCanvasClick,
      undoPoint,
      clearCurrent,
      confirmPolygon,
      confirmPoint,
      handleAddAlternate,
      handleRemoveAlternate,
      getPointTableData,
      deletePolygon,
      addTag,
      removeTag,
      handleSave,
      handleClose,
      getPolygonPoints,
      getCenter
    }
  }
}
</script>

<style lang="scss" scoped>
.area-annotation-dialog {
  :deep(.el-dialog__body) { padding: 10px 20px 20px; }
}
.full-height { height: 100%; }

/* ==== 左侧画布 ==== */
.left-canvas-col { height: 620px; display: flex; flex-direction: column; }
.map-canvas {
  flex: 1; width: 100%; background-color: #f7f9fc;
  border: 1px solid #409eff; border-radius: 4px;
  position: relative; overflow: hidden; cursor: crosshair;
  margin-bottom: 8px;
}
.map-bg-switch { position: absolute; top: 10px; right: 10px; z-index: 10; }
.map-background-img {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  object-fit: fill; user-select: none; pointer-events: none; z-index: 1;
}
.canvas-svg { position: absolute; top: 0; left: 0; user-select: none; z-index: 2; }
.map-placeholder-bg {
  position: absolute; top: 0; left: 0; width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: #909399; background-color: #f0f2f5; z-index: 1;
}
.map-tip-bar {
  background: #e8f4ff; border: 1px solid #d1e9ff;
  color: #409eff; padding: 8px 12px; border-radius: 4px;
  font-size: 12px; display: flex; align-items: center; gap: 6px;
}
.map-tip-bar .dot { width: 6px; height: 6px; background: #409eff; border-radius: 50%; }

/* ==== 右侧面板 ==== */
.right-panel-col { display: flex; flex-direction: column; height: 620px; text-align: left; }
.section-title { font-size: 15px; font-weight: 600; color: #303133; margin-bottom: 12px; }
.annotation-list { flex: 1; overflow-y: auto; margin-bottom: 16px; padding-right: 4px; }

/* 列表卡片 */
.annotation-item { 
  border: 1px solid #ebeef5; border-radius: 4px; 
  margin-bottom: 12px; background-color: #fff; 
  transition: all 0.3s;
}
.annotation-item.is-point { border-color: #dcdfe6; }
.item-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 12px; border-bottom: 1px solid #f0f2f5;
  background: #fafafa;
}
.item-title { display: flex; align-items: center; font-size: 14px; color: #303133; font-weight: 500;}
.icon-square-blue { width: 12px; height: 12px; border: 1px solid #409eff; border-radius: 2px; margin: 0 8px; }
.icon-square-orange { width: 12px; height: 12px; border: 1px solid #e6a23c; border-radius: 2px; margin: 0 8px; }
.icon-diamond { width: 10px; height: 10px; background: #409eff; transform: rotate(45deg); margin: 0 8px; }
/* 新增：充电桩图标 */
.icon-charger { width: 10px; height: 10px; border-radius: 50%; background: #67c23a; margin: 0 8px; }

.item-actions { display: flex; gap: 8px; align-items: center; }
.add-point-btn { color: #409eff; border-color: #b3d8ff; background: #ecf5ff; padding: 5px 11px; }
.add-point-btn:hover:not(.is-disabled) { background: #409eff; color: #fff; }
.delete-btn { border-color: #fbc4c4; color: #f56c6c; }
.delete-btn:hover { background: #fef0f0; }

.item-expand { padding: 12px; }

/* 折叠箭头 */
.css-arrow {
  display: inline-block; width: 0; height: 0;
  border-top: 4px solid transparent; border-bottom: 4px solid transparent;
  border-left: 5px solid #909399; transition: transform 0.2s ease-in-out;
}
.css-arrow.is-expanded { transform: rotate(90deg); }

/* 讲解点表格样式 */
.compact-table {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}
.compact-table :deep(.el-table__header th) {
  background-color: #fafafa;
  color: #606266;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 0;
  border-bottom: 1px solid #ebeef5;
}
.compact-table :deep(.el-table__row td) {
  padding: 4px 0;
  font-size: 12px;
  color: #303133;
  border-bottom: 1px solid #f5f5f5;
}
.compact-table :deep(.el-table__body tr:hover > td) {
  background-color: #f5f7fa;
}
.tag-main { color: #409eff; font-weight: bold; }
.tag-alt { color: #909399; font-weight: bold; }
.del-alt-btn { color: #f56c6c; font-size: 14px; padding: 0; }

/* 行为绑定面板 */
.behavior-panel {
  background: #fafafa; border: 1px solid #ebeef5; border-radius: 4px; padding: 10px 12px;
}
.panel-title {
  font-size: 13px; color: #303133; font-weight: 600;
  border-left: 3px solid #409eff; padding-left: 6px; margin-bottom: 10px;
}
/* 紧凑表单 */
.compact-behavior-form :deep(.el-form-item) { margin-bottom: 8px; }
.compact-behavior-form :deep(.el-form-item:last-child) { margin-bottom: 0; }
.compact-behavior-form :deep(.el-form-item__label) { font-size: 13px; color: #606266; padding-right: 8px; line-height: 28px; }
.compact-behavior-form :deep(.el-input__wrapper) { padding: 1px 8px; }
.compact-behavior-form :deep(.el-textarea__inner) { padding: 4px 8px; min-height: 28px !important; }

/* 标签展示区样式 */
.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  width: 100%;
}
.custom-tag {
  background: #ecf5ff;
  border-color: #d9ecff;
  color: #409eff;
}

/* ==== 新增表单 ==== */
.new-annotation-form {
  background-color: #fff; padding: 12px 16px;
  border-radius: 4px; border: 1px solid #ebeef5;
}
.compact-form :deep(.el-form-item) { margin-bottom: 10px; }
.compact-form :deep(.el-form-item__label) { font-size: 13px; padding-right: 8px; }

.canvas-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
.point-count { font-size: 13px; color: #606266; }
.action-btns { display: flex; gap: 8px; }
.full-width-add-btn { width: 100%; margin-top: 4px; border-radius: 4px; }

/* ==== 底部 ==== */
.dialog-footer { text-align: right; }
</style>